import React from "react"
import Pagination from "./Pagination"

/**
 * Reusable Table Component
 *
 * @param {Object} props
 * @param {Array} props.columns - Column definitions [{ key, label, render? }]
 * @param {Array} props.data - Table data
 * @param {Function} props.onRowClick - Row click handler
 * @param {string} props.className - Additional classes
 */
const Table = ({
  columns,
  data,
  onRowClick,
  className = "",
  noContainer = false,
  header,
  getRowKey,
  getRowClassName,
  getRowStyle,
  pagination,
  onPageChange,
  paginationProps,
  selectable = false,
  selectedRows = [],
  onSelectionChange,
  isRowSelectable,
  hideHeader = false,
  isLoading
}) => {
  const normalizedPagination = (() => {
    if (!pagination) return null

    const safePerPage =
      typeof pagination.perPage === "number" && pagination.perPage > 0
        ? pagination.perPage
        : 15

    const inferredTotal =
      typeof pagination.total === "number" && pagination.total > 0
        ? pagination.total
        : Array.isArray(data) && data.length > 0
          ? data.length
          : 0

    const safeLastPage =
      typeof pagination.lastPage === "number" && pagination.lastPage > 0
        ? pagination.lastPage
        : Math.max(1, Math.ceil(inferredTotal / safePerPage) || 1)

    const safeCurrentPage =
      typeof pagination.currentPage === "number" && pagination.currentPage > 0
        ? Math.min(pagination.currentPage, safeLastPage)
        : 1

    return {
      ...pagination,
      currentPage: safeCurrentPage,
      lastPage: safeLastPage,
      perPage: safePerPage,
      total: inferredTotal,
    }
  })()

  const getKey = (row, index) => {
    if (typeof getRowKey === "function") {
      return getRowKey(row, index)
    }
    return row.id !== undefined ? row.id : index
  }

  // Check if all selectable rows are selected
  const selectableData = data?.filter(
    (row, index) => !isRowSelectable || isRowSelectable(row, index)
  )
  const allSelected =
    selectable &&
    selectableData?.length > 0 &&
    selectableData?.every((row, index) => {
      const key = getKey(row, data.indexOf(row))
      return selectedRows.includes(key)
    })

  const someSelected = selectable && selectedRows.length > 0 && !allSelected

  // Handle select all
  const handleSelectAll = () => {
    if (!onSelectionChange) return

    if (allSelected) {
      // Deselect all current page rows
      const currentPageKeys = selectableData?.map((row, index) =>
        getKey(row, data.indexOf(row))
      )
      const newSelection = selectedRows.filter(
        (key) => !currentPageKeys.includes(key)
      )
      onSelectionChange(newSelection)
    } else {
      // Select all current page rows
      const currentPageKeys = selectableData?.map((row, index) =>
        getKey(row, data.indexOf(row))
      )
      const newSelection = [...new Set([...selectedRows, ...currentPageKeys])]
      onSelectionChange(newSelection)
    }
  }

  // Handle individual row selection
  const handleRowSelect = (row, rowIndex) => {
    if (!onSelectionChange) return

    const key = getKey(row, rowIndex)
    const isSelected = selectedRows.includes(key)

    if (isSelected) {
      onSelectionChange(selectedRows?.filter((k) => k !== key))
    } else {
      onSelectionChange([...selectedRows, key])
    }
  }

  const renderLoadingRows = () => {
    const colCount = columns.length + (selectable ? 1 : 0)

    return Array.from({ length: 5 }).map((_, i) => (
      <tr key={`loading-${i}`} className="animate-pulse">
        {Array.from({ length: colCount }).map((_, j) => (
          <td key={j} className="px-6 py-4">
            <div className="h-4 bg-gray-200 rounded w-full" />
          </td>
        ))}
      </tr>
    ))
  }

  const content = (
    <div className='overflow-x-auto'>
      <table className='min-w-full divide-y divide-gray-200'>
        {!hideHeader && (
          <thead className='bg-gray-50'>
            <tr>
              {selectable && (
                <th className='px-6 py-3 text-left w-12'>
                  <input
                    type='checkbox'
                    checked={allSelected}
                    ref={(input) => {
                      if (input) {
                        input.indeterminate = someSelected
                      }
                    }}
                    onChange={handleSelectAll}
                    className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer'
                  />
                </th>
              )}
              {columns.map((column) => (
                <th
                  key={column.key}
                  className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'
                >
                  {column.label}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody className='bg-white divide-y divide-gray-200'>
          {isLoading ? (
            renderLoadingRows()
          ) : data?.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length + (selectable ? 1 : 0)}
                className='px-6 py-12 text-center text-gray-500'
              >
                No data available
              </td>
            </tr>
          ) : (
            data?.map((row, rowIndex) => {
              const rowKey = getKey(row, rowIndex)
              const isSelected = selectedRows.includes(rowKey)
              const isSelectable =
                !isRowSelectable || isRowSelectable(row, rowIndex)

              return (
                <tr
                  key={rowKey}
                  onClick={(e) => {
                    // Don't trigger row click when clicking checkbox
                    if (e.target.type === "checkbox") return
                    onRowClick && onRowClick(row)
                  }}
                  className={
                    typeof getRowClassName === "function"
                      ? getRowClassName(row, rowIndex)
                      : `${onRowClick ? "cursor-pointer" : ""} ${isSelected ? "bg-blue-50" : "hover:bg-gray-50"
                      }`
                  }
                  style={
                    typeof getRowStyle === "function"
                      ? getRowStyle(row, rowIndex)
                      : undefined
                  }
                >
                  {selectable && (
                    <td className='px-6 py-4 whitespace-nowrap w-12'>
                      <input
                        type='checkbox'
                        checked={isSelected}
                        disabled={!isSelectable}
                        onChange={() => handleRowSelect(row, rowIndex)}
                        onClick={(e) => e.stopPropagation()}
                        className='h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer disabled:cursor-not-allowed disabled:opacity-50'
                      />
                    </td>
                  )}
                  {columns?.map((column) => (
                    <td
                      key={column.key}
                      className='px-6 py-4 whitespace-nowrap'
                    >
                      {column.render
                        ? column.render(row[column.key], row)
                        : row[column.key]}
                    </td>
                  ))}
                </tr>
              )
            })
          )}
        </tbody>
      </table>
    </div>
  )

  const paginationWrapperClassName = "mt-4"

  const paginationContent =
    pagination && typeof onPageChange === "function" ? (
      <div className={paginationWrapperClassName}>
        <Pagination
          currentPage={normalizedPagination?.currentPage}
          lastPage={normalizedPagination?.lastPage}
          perPage={normalizedPagination?.perPage}
          total={normalizedPagination?.total}
          onPageChange={onPageChange}
          {...(paginationProps || {})}
        />
      </div>
    ) : null

  if (noContainer) {
    return (
      <>
        {content}
        {paginationContent}
      </>
    )
  }

  return (
    <>
      <div
        className={`bg-white rounded-lg shadow overflow-hidden ${className}`}
      >
        {header ? (
          <div className='px-6 py-4 border-b border-gray-200'>{header}</div>
        ) : null}
        {content}
      </div>
      {paginationContent}
    </>
  )
}

export default Table
